import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../constants";
import { motion } from "framer-motion";
import PlayerLeaderboard from "../components/PlayerLeaderboard";
import AnimatedBackground from "../components/AnimatedBackground";
import { PicturePuzzleGameConfig } from "../types/games.ts";
import Confetti from "react-confetti";

// Types for Picture Puzzle game overview data
interface PlayerData {
  id: string;
  name: string;
  first_name: string | null; // First name for more personalized UI
  avatar: string | null;
  guesses: number;
  has_solved: boolean;
  has_given_up: boolean;
  solved_at: string | null;
  finished_timestamp: number | null; // Unix timestamp when player finished
  last_guess: string | null; // Player's last guess
  revealed_positions: number[]; // Positions of correctly guessed letters
  revealed_chars: Record<string, number[]>; // Correctly guessed letters grouped by character
  wrong_position: string[]; // Letters in the phrase but in wrong positions
  not_used: string[]; // Letters not in the phrase
  rank: number;
}

interface GameOverviewData {
  id: string;
  status: string;
  created: string;
  clue: string;
  answer_length: number;
  word_spans: [string, number, number][];
  image_url: string | null;
  players: PlayerData[];
  total_solved: number;
  total_given_up: number;
  letter_counts: Record<string, number>;
  special_chars_positions: [string, number][];
  config: PicturePuzzleGameConfig;
}

/**
 * GameOverview component displays a real-time view of a Picture Puzzle game
 * for use during podcasts or screen sharing sessions.
 */
// Component to show emoji confetti with a more controlled lifecycle
const EmojiConfetti = ({ active }: { active: boolean }) => {
  // Use internal state to control animation lifecycle
  const [isAnimating, setIsAnimating] = useState(false);

  // When active prop changes, manage the confetti lifecycle
  useEffect(() => {
    if (active && !isAnimating) {
      setIsAnimating(true);
      // Keep confetti visible for longer (10 seconds)
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [active, isAnimating]);

  if (!isAnimating) return null;

  return (
    <div className="fixed inset-0 w-full h-full pointer-events-none z-50 flex items-center justify-center">
      <div style={{ width: "100%", height: "100%" }}>
        <Confetti
          recycle={false}
          numberOfPieces={500}
          gravity={0.15} // Slower fall
          colors={[
            "#f44336",
            "#e91e63",
            "#9c27b0",
            "#673ab7",
            "#3f51b5",
            "#2196f3",
          ]}
        />
      </div>
    </div>
  );
};

const GameOverview = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const [gameData, setGameData] = useState<GameOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Fetch basic game data once (non-polling) - polling happens in the PlayerLeaderboard component
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(
          `${API_BASE_URL}/v2/games/live-overview/${gameId}`,
        );

        setGameData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching game data:", err);
        setError("Failed to load game data. Please try again.");
        setLoading(false);
      }
    };

    if (gameId) {
      // Fetch once
      fetchGameData();
    }
  }, [gameId]);

  // Loading state
  if (loading) {
    return (
      <>
        <AnimatedBackground />
        <div className="flex items-center justify-center h-screen">
          <motion.div
            className="relative"
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          >
            <div className="w-16 h-16 border-t-4 border-purple-300 border-solid rounded-full"></div>
            <div className="w-16 h-16 border-r-4 border-transparent border-solid rounded-full absolute top-0 opacity-75"></div>
          </motion.div>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <AnimatedBackground />
        <div className="flex items-center justify-center h-screen">
          <motion.div
            className="text-indigo-900 p-8 max-w-md bg-indigo-900/80 backdrop-blur-lg rounded-xl shadow-lg border border-purple-300/20"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-4 text-purple-100">
              Unexpected Error
            </h2>
            <p>{error}</p>
            <motion.button
              className="mt-4 px-4 py-2 bg-indigo-600 text-purple-100 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
              onClick={() => {
                // Refresh data by creating a new fetch call
                const refreshData = async () => {
                  try {
                    setLoading(true);
                    setError(null);
                    const response = await axios.get(
                      `${API_BASE_URL}/v2/games/live-overview/${gameId}`,
                    );
                    setGameData(response.data);
                    setLoading(false);
                  } catch (err) {
                    console.error("Error fetching game data:", err);
                    setError("Failed to load game data. Please try again.");
                    setLoading(false);
                  }
                };

                refreshData();
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Try Again
            </motion.button>
          </motion.div>
        </div>
      </>
    );
  }

  if (!gameData) {
    return (
      <>
        <AnimatedBackground />
        <div className="flex items-center justify-center h-screen">
          <motion.div
            className="text-purple-100 p-8 max-w-md bg-purple-900/80 backdrop-blur-lg rounded-xl shadow-lg border border-purple-300/20"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold mb-4">No Game Data Available</h2>
            <p>Sorry, we couldn't find any data for this game.</p>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center">
      {/* Confetti celebration shown in parent to prevent cutoff during polling */}
      <EmojiConfetti active={showConfetti} />
      <AnimatedBackground />

      {/* Main container with vertical layout */}
      <div className="relative z-10 flex flex-col items-center p-4 md:p-8 gap-12 w-full container mx-auto">
        {/* Centered heading */}
        <div className="relative">
          <motion.h1
            className="font-caprasimo text-brandLight text-7xl md:text-7xl text-center mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Picture Puzzle
          </motion.h1>
          <motion.h2 className="font-caprasimo text-brandLight/80 text-3xl md:text-3xl text-center mb-4 absolute right-0 bottom-[-35px]">
            by Braid
          </motion.h2>
        </div>

        {/* Content container for 3-column layout */}
        <div className="flex flex-col lg:flex-row items-start justify-between gap-6 container">
          {/* Left Column: Hint/Clue, Category, and phrase bubbles */}
          <div className="flex-shrink-0 w-full lg:w-1/4 min-w-[300px]">
            <div className="bg-brandLight backdrop-blur-lg rounded-xl shadow-2xl border-2 border-brand/50 overflow-hidden h-full">
              {/* Clue Section */}
              <div className="p-6 border-b-2 border-brand/10 pb-12">
                <h3 className="text-indigo-900 text-2xl font-semibold mb-3 ">
                  Hint
                </h3>
                <p className="font-semibold text-indigo-900 text-4xl">
                  {gameData?.clue || "Loading clue..."}
                </p>
              </div>

              {/* {/* Category Section */}
              {/* <div className="p-6 border-b-2 border-brand/10"> */}
              {/*   <h3 className="text-indigo-900 text-md  font-semibold mb-3"> */}
              {/*     Category */}
              {/*   </h3> */}
              {/*   <p className="text-indigo-900 font-semibold text-2xl"> */}
              {/*     {(gameData && gameData.config?.category) || "General"} */}
              {/*   </p> */}
              {/* </div> */}

              {/* Hangman-style answer bubbles */}
              <div className="px-6 py-10">
                <h3 className="text-indigo-900 text-2xl font-semibold mb-4">
                  Answer
                </h3>

                {/* The phrase bubbles - hangman style */}
                <div className="flex flex-col items-center gap-y-2">
                  {gameData &&
                  gameData.word_spans &&
                  gameData.word_spans.length > 0
                    ? gameData.word_spans.map(
                        ([_word, startIndex, endIndex], wordIdx) => (
                          <div
                            key={`word-${wordIdx}-${startIndex}`}
                            className="flex flex-row justify-center gap-1.5"
                          >
                            {Array.from({
                              length: endIndex - startIndex + 1,
                            }).map((_, charIdxInWord) => {
                              const overallCharIndex =
                                startIndex + charIdxInWord;

                              const isRevealed = false;
                              const revealedChar = "";
                              const specialCharEntry =
                                gameData.special_chars_positions.find(
                                  (p) => p[1] === overallCharIndex,
                                );
                              const specialChar = specialCharEntry
                                ? specialCharEntry[0]
                                : null;
                              const isSpecialCharPosition =
                                Boolean(specialChar);
                              const isSpace = specialChar === " ";
                              const shouldRevealLetter = false;

                              return (
                                <motion.div
                                  key={overallCharIndex}
                                  className={`w-8 h-8 flex items-center justify-center rounded-md border
                                ${
                                  isSpace
                                    ? "bg-white border-brandLight"
                                    : isSpecialCharPosition
                                      ? "bg-indigo-200/50 border-indigo-300/50"
                                      : isRevealed
                                        ? "bg-indigo-700 border-green-500"
                                        : "bg-indigo-200 border-indigo-400"
                                }
                                `}
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{
                                    opacity: 1,
                                    scale: shouldRevealLetter ? [1, 1.1, 1] : 1,
                                    backgroundColor: isRevealed
                                      ? "rgba(102, 126, 234, 0.8)"
                                      : isSpace
                                        ? "#F9F8FD"
                                        : "rgba(167, 139, 250, 0.3)",
                                  }}
                                  transition={{
                                    delay: overallCharIndex * 0.03,
                                    duration: 0.5,
                                    scale: { duration: 0.3 },
                                  }}
                                >
                                  <span
                                    className={`text-xl font-bold ${isSpace ? "text-transparent" : "text-white"}`}
                                  >
                                    {specialChar && !isSpace
                                      ? specialChar
                                      : shouldRevealLetter
                                        ? revealedChar
                                        : ""}
                                  </span>
                                </motion.div>
                              );
                            })}
                          </div>
                        ),
                      )
                    : gameData && gameData.answer_length > 0
                      ? // Fallback to original rendering if word_spans is not available
                        Array.from(
                          { length: gameData.answer_length },
                          (_, index) => {
                            const isRevealed = false;
                            const revealedChar = "";
                            const specialCharEntry =
                              gameData.special_chars_positions.find(
                                (p) => p[1] === index,
                              );
                            const specialChar = specialCharEntry
                              ? specialCharEntry[0]
                              : null;
                            const isSpecialCharPosition = Boolean(specialChar);
                            const isSpace = specialChar === " ";
                            const shouldRevealLetter = false;

                            return (
                              <motion.div
                                key={index}
                                className={`w-8 h-8 flex items-center justify-center rounded-md border
                            ${
                              isSpace
                                ? "bg-white border-brandLight"
                                : isSpecialCharPosition
                                  ? "bg-indigo-200/50 border-indigo-300/50"
                                  : isRevealed
                                    ? "bg-indigo-700 border-green-500"
                                    : "bg-indigo-200 border-indigo-400"
                            }
                            `}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{
                                  opacity: 1,
                                  scale: shouldRevealLetter ? [1, 1.1, 1] : 1,
                                  backgroundColor: isRevealed
                                    ? "rgba(102, 126, 234, 0.8)"
                                    : isSpace
                                      ? "#F9F8FD"
                                      : "rgba(167, 139, 250, 0.3)",
                                }}
                                transition={{
                                  delay: index * 0.03,
                                  duration: 0.5,
                                  scale: { duration: 0.3 },
                                }}
                              >
                                <span
                                  className={`text-xl font-bold ${isSpace ? "text-transparent" : "text-white"}`}
                                >
                                  {specialChar && !isSpace
                                    ? specialChar
                                    : shouldRevealLetter
                                      ? revealedChar
                                      : ""}
                                </span>
                              </motion.div>
                            );
                          },
                        )
                      : null}
                </div>
              </div>
            </div>
          </div>

          {/* Center Column: Picture puzzle image */}
          <div className="flex-shrink-0 w-full lg:w-2/5">
            <div className="bg-brandLight backdrop-blur-lg rounded-xl shadow-2xl border-2 border-brand/50 overflow-hidden">
              <div className="p-6">
                {gameData?.image_url ? (
                  <motion.img
                    src={gameData.image_url}
                    alt="Picture Puzzle Clue"
                    className="w-full h-auto object-contain rounded-lg"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  />
                ) : (
                  <div className="w-full h-[500px] bg-gray-800/50 rounded-lg flex items-center justify-center text-gray-400 text-2xl">
                    Loading Image...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Leaderboard Container */}
          <div className="flex-shrink-0 w-full lg:w-1/3 min-w-[350px]">
            {gameId && (
              <PlayerLeaderboard
                gameId={gameId}
                onPlayerSolved={() => {
                  // Simply trigger the confetti - its component will handle the timing
                  setShowConfetti(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { GameOverview };
